// This is probably the most "meta-y" of all the snippets here. It's a cusotm hook to
// control the state of a checkbox group. It starts with the "Anyone" checkbox checked
// but will control the state of other checkbox groups as the user interacts with the
// various subgroups in the ruleset.

import { useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { assertPresence } from '@bonusly/runtime';

type UseGroupRulesControlOptions = {
	forceScope?: string;
};

// TODO: The components in this directory need to be polished a bit. Originally this was all part of nominations, but now we have to
// do the same thing in celebrations. Unfortunately the way we handle rules is very... unclean. The API data doesn't match how
// we want to use it in forms, which has turned this into a pretty big mess. Ticket SHARK-2433 handles a spike on how to do this.
export const useGroupRulesControl = (
	name: string,
	{ formState: { defaultValues }, control }: ReturnType<typeof useForm<any>>,
	options: UseGroupRulesControlOptions = {}
) => {
	const upcase = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

	assertPresence(defaultValues);

	const hasAny = (key: string) => {
		const values = defaultValues[`${name}${upcase(key)}`];
		return values ? values.filter(Boolean).length > 0 : false;
	};

	const [checked, setChecked] = useState(() => {
		return {
			locations: hasAny('locations'),
			departments: hasAny('departments'),
			teams: hasAny('teams'),
			roles: hasAny('roles'),
		};
	});

	const anyChecked = Object.values(checked).some(Boolean);

	// We don't want to define an array for roles include them for celebrations and don't want to dirty the form by defining it here.
	const { remove: removeLocations } = useFieldArray({ control, name: `${name}${upcase('locations')}` });
	const { remove: removeDepartments } = useFieldArray({ control, name: `${name}${upcase('departments')}` });
	const { remove: removeTeams } = useFieldArray({ control, name: `${name}${upcase('teams')}` });

	const clearValues = () => {
		removeLocations();
		removeDepartments();
		removeTeams();
	};

	const handleChange = (key: keyof typeof checked) => () => {
		setChecked({
			locations: false,
			departments: false,
			teams: false,
			roles: false,
			[key]: true,
		});

		clearValues();
	};

	const buildHandlers = (name: keyof typeof checked) => {
		return {
			isChecked: checked[name] || !!(options.forceScope && name.includes(options.forceScope)),
			onChange: handleChange(name),
		};
	};

	return {
		anyone: {
			isChecked: !anyChecked,
			onChange: () => {
				setChecked({
					locations: false,
					departments: false,
					teams: false,
					roles: false,
				});

				clearValues();
			},
		},
		locations: buildHandlers('locations'),
		departments: buildHandlers('departments'),
		teams: buildHandlers('teams'),
		roles: buildHandlers('roles'),
	};
};

type SelectedGroupsResult = {
	groupContext: string | null;
	selectedGroups: string[];
};

export const useSelectedGroups = (
	control: ReturnType<typeof useGroupRulesControl>,
	locations: (string | undefined)[],
	departments: (string | undefined)[],
	teams: (string | undefined)[],
	roles: (string | undefined)[],
	rules: {
		locations: { key: string; name: string }[];
		departments: { key: string; name: string }[];
		teams: { key: string; name: string }[];
		roles: { key: string; name: string }[];
	}
): SelectedGroupsResult => {
	return useMemo(() => {
		let groupContext: string | null = null;
		let selectedGroups: string[] = [];

		if (control.departments.isChecked) {
			groupContext = 'departments';
			selectedGroups = departments
				.filter((item): item is string => Boolean(item))
				.map((item) => {
					return rules.departments.find((rule) => rule.key === item)?.name ?? item;
				});
		} else if (control.locations.isChecked) {
			groupContext = 'locations';
			selectedGroups = locations
				.filter((item): item is string => Boolean(item))
				.map((item) => {
					return rules.locations.find((rule) => rule.key === item)?.name ?? item;
				});
		} else if (control.teams.isChecked) {
			groupContext = 'teams';
			selectedGroups = teams
				.filter((item): item is string => Boolean(item))
				.map((item) => {
					return rules.teams.find((rule) => rule.key === item)?.name ?? item;
				});
		} else if (control.roles.isChecked) {
			selectedGroups = roles
				.filter((item): item is string => Boolean(item))
				.map((item) => {
					return rules.roles.find((rule) => rule.key === item)?.name ?? item;
				});
			if (selectedGroups.length > 0) {
				if (selectedGroups[0] === 'Managers') {
					groupContext = 'isManager';
				} else {
					groupContext = 'isNotManager';
				}
			}
		} else if (control.anyone.isChecked) {
			groupContext = 'anyone';
			selectedGroups = [groupContext];
		}

		return {
			groupContext,
			selectedGroups,
		};
	}, [control, locations, departments, teams, roles, rules]);
};
